import { clonePlainValue } from "../../Firestore/nutrition/OptimisticNutritionEntity";
import {
  createInitialPlanDraft,
  getPlanMealsForSave,
} from "./nutritionPlanLogic";
import {
  getNutritionPlanDisplayNameFromSources,
  resolveNutritionPlanName,
} from "./nutritionPlanNameUtils";

export const NUTRITION_PLAN_ACTION_INCOMPLETE_ERROR =
  "nutrition_plan_action_incomplete";

function normalizePlanId(value) {
  return String(value ?? "").trim().toLowerCase();
}

function getNonNegativeNumber(value) {
  const resolvedValue = Number(value);
  if (!Number.isFinite(resolvedValue) || resolvedValue < 0) {
    return null;
  }

  return resolvedValue;
}

function resolvePersistedExpectedMealCount(rootRecord = null, fallbackPlan = null) {
  return (
    getNonNegativeNumber(rootRecord?.meal_count) ??
    getNonNegativeNumber(fallbackPlan?.meal_count)
  );
}

function buildActionPlanRecord(sourcePlan = {}, fallbackPlan = null) {
  const normalizedDraft = createInitialPlanDraft(sourcePlan ?? {});

  return {
    ...clonePlainValue(normalizedDraft),
    name:
      fallbackPlan == null
        ? String(normalizedDraft?.name ?? "")
        : resolveNutritionPlanName(normalizedDraft, fallbackPlan),
    meals: getPlanMealsForSave(normalizedDraft),
  };
}

function mergeLocalSafeTopLevelFields(basePlan = {}, localDraft = {}) {
  return buildActionPlanRecord({
    ...basePlan,
    name: String(localDraft?.name ?? ""),
    notes: String(localDraft?.notes ?? ""),
    goals: clonePlainValue(localDraft?.goals ?? basePlan?.goals ?? null),
  });
}

function canTrustPersistedMealSnapshot(mealRecords = [], expectedMealCount = null) {
  const actualMealCount = Array.isArray(mealRecords) ? mealRecords.length : 0;
  if (expectedMealCount != null) {
    return actualMealCount === expectedMealCount;
  }

  return actualMealCount > 0;
}

function canTrustLocalDraft(localDraft = {}, expectedMealCount = null) {
  const localMealCount = getPlanMealsForSave(localDraft).length;
  if (localMealCount > 0) {
    return true;
  }

  return expectedMealCount === 0;
}

export function createNutritionPlanActionIncompleteError({
  planId = "",
  planName = "",
  planNames = [],
  reason = "incomplete_snapshot",
} = {}) {
  const normalizedPlanNames = [...new Set(
    (Array.isArray(planNames) ? planNames : [planName])
      .map((value) => String(value ?? "").trim())
      .filter(Boolean),
  )];
  const error = new Error("Nutrition plan is not fully loaded");

  error.code = NUTRITION_PLAN_ACTION_INCOMPLETE_ERROR;
  error.planId = normalizePlanId(planId);
  error.planName = normalizedPlanNames[0] ?? String(planName ?? "").trim();
  error.planNames = normalizedPlanNames;
  error.reason = reason;

  return error;
}

export function isNutritionPlanActionIncompleteError(error) {
  return error?.code === NUTRITION_PLAN_ACTION_INCOMPLETE_ERROR;
}

export function getNutritionPlanActionErrorDescription(
  error,
  fallbackDescription = "This nutrition plan is not fully loaded yet, so the action could not be completed.",
  t = null,
) {
  const resolvedFallbackDescription =
    fallbackDescription === ""
      ? ""
      : typeof t === "function"
        ? t(
            "nutritionPlanActionIncompleteFallback-string",
            fallbackDescription,
          )
        : fallbackDescription;
  if (!isNutritionPlanActionIncompleteError(error)) {
    return resolvedFallbackDescription;
  }

  const planNames = Array.isArray(error?.planNames)
    ? error.planNames.filter(Boolean)
    : [];

  if (planNames.length > 1) {
    return typeof t === "function"
      ? t(
          "nutritionPlanActionIncompleteMultiple-string",
          "These nutrition plans are not fully loaded yet, so the action could not be completed: {{planNames}}.",
          { planNames: planNames.join(", ") },
        )
      : `These nutrition plans are not fully loaded yet, so the action could not be completed: ${planNames.join(", ")}.`;
  }

  if (planNames.length === 1) {
    return typeof t === "function"
      ? t(
          "nutritionPlanActionIncompleteSingle-string",
          '"{{name}}" is not fully loaded yet, so the action could not be completed.',
          { name: planNames[0] },
        )
      : `"${planNames[0]}" is not fully loaded yet, so the action could not be completed.`;
  }

  return resolvedFallbackDescription;
}

function resolveActionPlanName(sources = [], language = "en") {
  return getNutritionPlanDisplayNameFromSources(sources, language);
}

export async function resolveNutritionPlanActionSnapshot({
  planId,
  sourceEntity = null,
  sourceRootRecord = null,
  fallbackPlan = null,
  loadMealRecords,
  language = "en",
} = {}) {
  const normalizedPlanId = normalizePlanId(
    planId ?? sourceEntity?.id ?? sourceRootRecord?.id ?? fallbackPlan?.id,
  );
  if (!normalizedPlanId || typeof loadMealRecords !== "function") {
    throw createNutritionPlanActionIncompleteError({
      planId: normalizedPlanId,
      planName: resolveActionPlanName(
        [sourceEntity?.getDraftSnapshot?.(), sourceEntity, sourceRootRecord, fallbackPlan],
        language,
      ),
      reason: "missing_inputs",
    });
  }

  const planName = resolveActionPlanName(
    [sourceEntity?.getDraftSnapshot?.(), sourceEntity, sourceRootRecord, fallbackPlan],
    language,
  );
  const expectedMealCount = resolvePersistedExpectedMealCount(
    sourceRootRecord,
    fallbackPlan,
  );
  const localDraft =
    sourceEntity && typeof sourceEntity?.getDraftSnapshot === "function"
      ? sourceEntity.getDraftSnapshot()
      : null;

  if (localDraft && sourceEntity?.hasUnsyncedChanges === true) {
    if (canTrustLocalDraft(localDraft, expectedMealCount)) {
      const localPlanRecord = buildActionPlanRecord(localDraft);
      return {
        planId: normalizedPlanId,
        planName,
        planRecord: localPlanRecord,
        mealRecords: localPlanRecord.meals,
        rootRecord: sourceRootRecord ?? null,
        sourceKind: "local_draft",
      };
    }

    const fetchedMealRecords = await loadMealRecords(normalizedPlanId, {
      force: true,
    });
    if (!canTrustPersistedMealSnapshot(fetchedMealRecords, expectedMealCount)) {
      throw createNutritionPlanActionIncompleteError({
        planId: normalizedPlanId,
        planName,
        reason: "forced_fetch_incomplete",
      });
    }

    const mergedPlanRecord = mergeLocalSafeTopLevelFields(
      {
        ...(sourceRootRecord ?? fallbackPlan ?? {}),
        name: resolveNutritionPlanName(sourceRootRecord, fallbackPlan),
        id: normalizedPlanId,
        meals: fetchedMealRecords,
      },
      localDraft,
    );
    return {
      planId: normalizedPlanId,
      planName,
      planRecord: mergedPlanRecord,
      mealRecords: mergedPlanRecord.meals,
      rootRecord: sourceRootRecord ?? null,
      sourceKind: "merged_local_fields",
    };
  }

  if (localDraft) {
    const entityPlanRecord = buildActionPlanRecord(localDraft);
    if (
      sourceEntity?.mealsHydrated === true &&
      canTrustPersistedMealSnapshot(entityPlanRecord.meals, expectedMealCount)
    ) {
      return {
        planId: normalizedPlanId,
        planName,
        planRecord: entityPlanRecord,
        mealRecords: entityPlanRecord.meals,
        rootRecord: sourceRootRecord ?? null,
        sourceKind: "entity_draft",
      };
    }
  }

  const fetchedMealRecords = await loadMealRecords(normalizedPlanId, {
    force: true,
  });
  if (!canTrustPersistedMealSnapshot(fetchedMealRecords, expectedMealCount)) {
    throw createNutritionPlanActionIncompleteError({
      planId: normalizedPlanId,
      planName,
      reason: "persisted_snapshot_incomplete",
    });
  }

  const persistedPlanRecord = buildActionPlanRecord(
    {
      ...(sourceRootRecord ?? fallbackPlan ?? {}),
      name: resolveNutritionPlanName(sourceRootRecord, fallbackPlan),
      id: normalizedPlanId,
      meals: fetchedMealRecords,
    },
    fallbackPlan,
  );
  return {
    planId: normalizedPlanId,
    planName,
    planRecord: persistedPlanRecord,
    mealRecords: persistedPlanRecord.meals,
    rootRecord: sourceRootRecord ?? null,
    sourceKind: "persisted_fetch",
  };
}
